import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, FormGroup, HelpBlock, Modal } from "components/bootstrap";
import SegmentedControl from "components/bootstrap/SegmentedControl";
import { ModalButtonToolbar, Select, Spinner } from "components/common";
import { useEventNotifications } from "components/event-notifications/hooks/useEventNotifications";
import HealthStatusIcon from "health/HealthStatusIcon";
import { getStatusLabel } from "health/healthStatusCopy";
import useHistory from "routing/useHistory";
import Routes from "routing/Routes";
import STATUS_OPTIONS from "./statusOptions";
import toHealthAlertTarget from "./target";
import useCreateHealthAlert from "./useCreateHealthAlert";
import { STATUS_PRESETS } from "./types";
import ValidationErrors from "./ValidationErrors";
const DEFAULT_STATUS_PRESET = "warning_or_critical";
const HealthCheckSummary = styled.div(
  ({ theme }) => `
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    column-gap: ${theme.spacings.sm};
    row-gap: ${theme.spacings.xs};
    padding: ${theme.spacings.xs} ${theme.spacings.sm};
    border: 1px solid ${theme.colors.variant.lighter.default};
    border-radius: 4px;
  `
);
const HealthCheckPath = styled.span(
  ({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacings.xs};
    overflow-wrap: break-word;
  `
);
const HealthCheckStatus = styled.span(
  ({ theme }) => `
    flex-shrink: 0;
    white-space: nowrap;
    font-size: ${theme.fonts.size.small};
    color: ${theme.colors.gray[60]};
  `
);
const CreateHealthAlertModal = ({ show, onHide, node, path }) => {
  var _a;
  const { t } = useTranslation("enterprise_license");
  const [statusPreset, setStatusPreset] = useState(DEFAULT_STATUS_PRESET);
  const [notificationId, setNotificationId] = useState(void 0);
  const [fieldErrors, setFieldErrors] = useState(void 0);
  const { data: notificationsData, isLoading: isLoadingNotifications } = useEventNotifications();
  const { createHealthAlert, isCreating } = useCreateHealthAlert();
  const { push } = useHistory();
  const notificationOptions = ((_a = notificationsData == null ? void 0 : notificationsData.notifications) != null ? _a : []).map((notification) => ({
    value: notification.id,
    label: notification.title
  }));
  const handleCreate = () => {
    setFieldErrors(void 0);
    createHealthAlert({
      targets: [toHealthAlertTarget(node.id)],
      statuses: STATUS_PRESETS[statusPreset],
      notification_id: notificationId
    }).then((created) => {
      onHide();
      push(Routes.ALERTS.DEFINITIONS.show(created.id));
    }).catch((error) => {
      var _a2, _b;
      if (error.status === 400 && ((_b = (_a2 = error.additional) == null ? void 0 : _a2.body) == null ? void 0 : _b.errors)) {
        setFieldErrors(error.additional.body.errors);
      }
    });
  };
  return /* @__PURE__ */ React.createElement(Modal, { show, onHide }, /* @__PURE__ */ React.createElement(Modal.Header, { showCloseButton: true }, /* @__PURE__ */ React.createElement(Modal.Title, null, t("health.alert.modal_title"))), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement("p", null, t("health.alert.modal_description")), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement("label", { htmlFor: "health-alert-target" }, t("health.alert.health_check")), /* @__PURE__ */ React.createElement(HealthCheckSummary, { id: "health-alert-target", "data-testid": "health-alert-target" }, /* @__PURE__ */ React.createElement(HealthCheckPath, null, /* @__PURE__ */ React.createElement(HealthStatusIcon, { status: node.status, title: getStatusLabel(node.status) }), path.map((segment, index) => /* @__PURE__ */ React.createElement(React.Fragment, { key: segment.id }, index > 0 ? " / " : null, index === path.length - 1 ? /* @__PURE__ */ React.createElement("strong", null, segment.title) : segment.title))), /* @__PURE__ */ React.createElement(HealthCheckStatus, null, t("health.alert.currently", { status: getStatusLabel(node.status) }))), /* @__PURE__ */ React.createElement(ValidationErrors, { errors: fieldErrors == null ? void 0 : fieldErrors.targets })), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement("label", { htmlFor: "health-alert-status" }, t("health.alert.alert_when")), /* @__PURE__ */ React.createElement("div", { id: "health-alert-status" }, /* @__PURE__ */ React.createElement(
    SegmentedControl,
    {
      data: STATUS_OPTIONS,
      value: statusPreset,
      color: "neutral",
      autoContrast: true,
      onChange: setStatusPreset
    }
  )), /* @__PURE__ */ React.createElement(ValidationErrors, { errors: fieldErrors == null ? void 0 : fieldErrors.statuses })), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement("label", { htmlFor: "health-alert-notification" }, t("health.alert.notification_optional")), isLoadingNotifications ? /* @__PURE__ */ React.createElement(Spinner, { text: t("health.alert.loading_notifications") }) : /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: "health-alert-notification",
      placeholder: t("health.alert.no_notification"),
      options: notificationOptions,
      value: notificationId,
      onChange: (value) => setNotificationId(value || void 0)
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, t("health.alert.notification_help")), /* @__PURE__ */ React.createElement(ValidationErrors, { errors: fieldErrors == null ? void 0 : fieldErrors.notification_id }))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(ModalButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { onClick: onHide, disabled: isCreating }, t("common:actions.cancel")), /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: handleCreate, disabled: isCreating }, isCreating ? t("health.alert.creating") : t("health.alert.create_alert")))));
};
export default CreateHealthAlertModal;
